import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, catchError, filter, first, map, mergeMap, Observable, of, skipWhile, Subject, take, takeUntil } from 'rxjs';
import { LgvAPIService, LgvClientService, LgvUserService, NotificationService } from '../../core/core.module';
import { LgvLoanApplicationService } from '../../core/services/lgv-loan-application.service';
import { LgvDataHelper } from '../../core/utility/lgv-data-helper';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvModel } from '../../data-models/longevity.model';
import { StdObj } from '../../data-models/standard-objects';
import { InfoPopup } from '../info-popup/info-popup.component';
import { LoanAppTaskModalComponent } from '../loan-app-task-modal/loan-app-task-modal.component';
import { LgvUserStateLicensingService } from '../../core/services/lgv-user-licensing.service';
import { LgvStatesHelper } from '../../core/utility/lgv-states-helper';

@Component({
    selector: 'lgv-start-loan-modal',
    templateUrl: './start-loan-modal.component.html',
    styleUrls: ['./start-loan-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StartLoanModalComponent implements OnInit, OnDestroy {

    agentDTO: LgvModel.AgentDTO;
    opportunity: LgvModel.Opportunity;

    _clientInfo: LgvModel.ClientInfo = null;
    clientInfo$ = new BehaviorSubject(this._clientInfo);

    onDestroy$ = new Subject();

    isLoading$ = new BehaviorSubject(false);
    pageMode$ = new BehaviorSubject('loanApp');

    get pageModeLoanApp$(): Observable<boolean> { return this.pageMode$.pipe(takeUntil(this.onDestroy$), map(x => x === 'loanApp')) }
    get pageModeFinished$(): Observable<boolean> { return this.pageMode$.pipe(takeUntil(this.onDestroy$), map(x=> x === 'finished'))  }
    get pageModeTaskSets$(): Observable<boolean> { return this.pageMode$.pipe(takeUntil(this.onDestroy$), map(x => x === 'taskSets')) }

    _selectedAssistant: LgvModel.UserDTO = null;
    selectedAssistant$ = new BehaviorSubject(this._selectedAssistant);

    _selectedProcessor: LgvModel.UserDTO = null;
    selectedProcessor$ = new BehaviorSubject(this._selectedProcessor);

    _selectedProperty: LgvModel.Property = null;
    selectedProperty$ = new BehaviorSubject(this._selectedProperty);

    _properties: LgvModel.Property[] = [];
    properties$ = new BehaviorSubject(this._properties);

    _selectedLoan: LgvModel.Loan = null;
    selectedLoan$ = new BehaviorSubject(this._selectedLoan);

    _loans: LgvModel.Loan[] = [];
    loans$ = new BehaviorSubject(this._loans);

    taskSets: any[] = [];
    selectedInfoTaskSet = null;

    _taskList: LgvLoanApp.Task[] = [];
    taskList$ = new BehaviorSubject(this._taskList);

    currentAgent = '';
    currentCompany = '';
    currentOrganization = '';
    selectedClient = null;

    LoanPurposeList: StdObj.ListItem[];

    get LendingPadLoanPurposeList(): StdObj.ListItem[] {
        return [
            { ID: 'Purchase', Display: 'Purchase' },
            { ID: 'Refinance', Display: 'Refinance' },
            // { ID: 'No Cash Out Refinance', Display: 'No Cash Out Refinance' },
            // { ID: 'Construction', Display: 'Construction' },
            // { ID: 'Construction Permanent', Display: 'Construction Permanent' },
            // { ID: 'Other', Display: 'Other' },
        ];
    }


    //#region Main Form
    _form: UntypedFormGroup;

    get stateField(): AbstractControl { return this._form.get('state'); }
    get propertyField(): AbstractControl { return this._form.get('propertyId'); }
    get taskSetField(): AbstractControl { return this._form.get('taskSetId'); }
    get loanField(): AbstractControl { return this._form.get('loanId'); }
    get assistantField(): AbstractControl { return this._form.get('assistant'); }
    get processorField(): AbstractControl { return this._form.get('processor'); }
    get loanPurposeField(): AbstractControl { return this._form.get('loanPurpose'); }
    //#endregion Main Form

    currentUserId$ = this.lgvUserSvc.DBUserInfo$.pipe(
        filter(user => !!user),
        map(user => {
            console.log('StartLoanModal: Current user ID', user.id);
            return user.id;
        })
    );
    currentAgentId$ = this.clientInfo$.pipe(
        map(user => {
            console.log('StartLoanModal: Current user ID', user.id);
            return user.owner.id;
        })
    );

    private _selectedAgentId = new BehaviorSubject<string>(null);
    selectedAgentId$ = this._selectedAgentId.asObservable();

    constructor(
        private http: HttpClient,
        private lgvClientSvc: LgvClientService,
        private fb: UntypedFormBuilder,
        public notificationSvc: NotificationService,
        public lgvDataHlpr: LgvDataHelper,
        public dialogRef: MatDialogRef<StartLoanModalComponent>,
        public lgvUserSvc: LgvUserService,
        public matDialog: MatDialog,
        public lgvLoanAppSvc: LgvLoanApplicationService,
        private lgvApiSvc: LgvAPIService,
        private lgvUserStateLicensingSvc: LgvUserStateLicensingService,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {

        this.agentDTO = data.agentDTO;
        this.opportunity = data.opportunity;
        // Reset client info when modal is opened
        this._clientInfo = null;
        this.clientInfo$.next(null);
        this._selectedProperty = null;
        this.selectedProperty$.next(null);
        this._loans = [];
        this.loans$.next([]);
        this._selectedLoan = null;
        this.selectedLoan$.next(null);

        // If in client mode and we have a client ID, set the selectedClient
        if (data?.isClientMode && data?.clientId) {
            this.selectedClient = { id: data.clientId, ownerId: data.ownerId };
        } else {
            this.selectedClient = null;
        }
    }


    ngOnInit(): void {
        // Reset the form and data
        this.initForm();
        this.taskList$.next([]);

        // If in client mode and we have a client ID, load the client data
        if (this.data?.isClientMode && this.selectedClient?.id) {
            this.isLoading$.next(true);
            this.lgvClientSvc.LoadClient(this.selectedClient.id).pipe(
                take(1),
                catchError(error => {
                    this.notificationSvc.error('Failed to load client information. Please try again.');
                    console.error('Error loading client:', error);
                    this.dialogRef.close({ action: false });
                    return of(null);
                })
            ).subscribe(client => {
                if (client) {
                    this.clientInfo$.next(client);
                    this.selectedClient = client;

                    // Set client ID in form
                    if (this._form && client.id) {
                        this._form.get('clientId').setValue(client.id);
                    }
                }
                this.isLoading$.next(false);
            });
        }

        // Ensure we only respond to client info changes for the current client
        this.lgvClientSvc.ClientInfo$.pipe(
            takeUntil(this.onDestroy$),
            filter(client => client !== null &&
                (this.selectedClient === null || client.id === this.selectedClient.id))
        ).subscribe(client => {
            this.clientInfo$.next(client);
        });

        this.lgvClientSvc.ownedAndProspectProperties$.pipe(
            takeUntil(this.onDestroy$),
            filter(f => f !== null)
        ).subscribe(x => {
            this.properties$.next(x);
            this.loans$.next([]);
            this.selectedLoan$.next(null);
        });

        this.lgvLoanAppSvc.taskSets$.pipe(
            takeUntil(this.onDestroy$),
            filter(f => !!f && f.length > 0),
            take(1)
        ).subscribe(x => {
            this.taskSets = x; // Store the task sets
            // select the first item in the list
            if (x.length > 0) {
                this.selectedInfoTaskSet = x[0];
            }
        });

        this.LoanPurposeList = this.LendingPadLoanPurposeList;
    }

    initForm() {
        this._form = this.fb.group({
            state: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(2), ValidStateCode()]],
            propertyId: [null, []],
            loanId: [null, []],
            loanPurpose: [null, [Validators.required]],
            taskSetId: [null, []],
            clientId: [this.selectedClient?.id],
            agentId: [null],
            borrowerFirstName: [null],
            borrowerLastName: [null],
            borrowerEmail: [null],
            borrowerPhone: [null, [Validators.maxLength(10)]]
        });

        this.stateField.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((x) => {
            // force upper case for state code
            if (x === null || x === undefined) return;

            let stateUpperCase = (x as string).toUpperCase();
            if (this.stateField.getRawValue() !== stateUpperCase) {
                this.stateField.setValue(stateUpperCase);
            }
        });

        if (!this.data?.isClientMode) {
            this._form.get('borrowerFirstName').addValidators(Validators.required);
            this._form.get('borrowerLastName').addValidators(Validators.required);
            this._form.get('borrowerEmail').addValidators(Validators.required);
            this._form.get('borrowerPhone').addValidators(Validators.required);

            // Only add required validator for taskSetId if not pre-licensed agent and not client mode
            this.lgvUserSvc.IsPreLicensedAgent$.pipe(first()).subscribe(isPreLicensed => {
                if (!isPreLicensed && !this.data?.isClientMode) {
                    this._form.get('taskSetId').addValidators(Validators.required);
                    this._form.get('taskSetId').updateValueAndValidity();
                }
            });
        }

        if(!this.data?.isClientMode && this.lgvUserSvc.IsAnyAdmin) {
            this._form.get('agentId').addValidators(Validators.required);
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();

        this.clientInfo$.next(null);
        this.selectedProperty$.next(null);
        this.loans$.next([]);
        this.selectedLoan$.next(null);
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }

    onFinished() {
        this.dialogRef.close({ action: true });
    }

    onSubmit() {
        this.isLoading$.next(true);
        // create client first if none yet, before starting loan app
        if (!this.data?.isClientMode && !this.selectedClient) {
            this.lgvClientSvc.SaveClient({
                firstName:  this._form.get('borrowerFirstName').value,
                lastName:  this._form.get('borrowerLastName').value,
                email:  this._form.get('borrowerEmail').value,
                mobilePhone:  this._form.get('borrowerPhone').value,
                isPropertylessClient: true,
                ownerId: this.agentDTO.agentInfo.id,
            }).pipe(take(1), mergeMap(clientResponse => {
                return this.lgvClientSvc.LoadClient(clientResponse.id);
            })).subscribe((client) => {
                this.clientInfo$.next(client);
                this.selectedClient = client;
                this.startAPILoanApp();
            });
        } else {
            this.startAPILoanApp();
        }
    }

    selectClient(client) {
        if (!client || typeof client === 'string') {
            console.log('StartLoanModal: Invalid client value, clearing client data');
            this.selectedClient = null;
            this._form.get('clientId').setValue(null);
            return;
        }

        this.selectedClient = client;
        this.clientInfo$.next(client);
        this.lgvClientSvc.LoadSnapshotForNonClient(client?.id);
        this._form.get('clientId').setValue(client.id);
        this._form.get('borrowerFirstName').setValue(client?.firstName);
        this._form.get('borrowerLastName').setValue(client?.lastName);
        this._form.get('borrowerEmail').setValue(client?.email);
        this._form.get('borrowerPhone').setValue(client?.mobilePhone);
    }

    selectAgent(agent) {
        if (!agent || typeof agent === 'string') {
            console.log('StartLoanModal: Invalid agent value, clearing agent data');
            this.agentDTO = null;
            this._form.get('agentId').setValue(null);
            this._selectedAgentId.next(null);
            return;
        }
        
        this.agentDTO = agent;
        this.agentDTO.agentInfo = agent;
        this._form.get('agentId').setValue(agent.id);
        this._selectedAgentId.next(agent.id);
    }

    startAPILoanApp() {
        let client= this.clientInfo$.value; 

        if (!client) {
            this.notificationSvc.error('Unable to obtain client infomation.');
            return;
        }
        
        let appModel = this.lgvLoanAppSvc.buildLoanAppModel(
            this.opportunity,
            this.selectedProperty$.value,
            [client],
            this.selectedLoan$.value,
            this.agentDTO,
            this.selectedAssistant$.value,
            this.selectedProcessor$.value,
            null, // rawBorrowerModel
            null, // rawLoanModel
            this.loanPurposeField.value,
            this.stateField.value
        );

        appModel.tasks = this.buildApplicationTasks();

        console.log('tasks');
        // console.log(appModel.tasks);
        console.log('Loan App Model:');
        // console.log(appModel);

        this.isLoading$.next(true);

        this.lgvLoanAppSvc.startLoanApplication(this.agentDTO?.agentInfo?.id, appModel).pipe(first(), takeUntil(this.onDestroy$)).subscribe(loanAppResult => {
            //console.log('Result from new Loan App:');
            //console.log(loanAppResult);

            this.isLoading$.next(false);
            let loanAppId = loanAppResult["id"];

            if (!!loanAppId) {
                this.pageMode$.next('finished');
            }
            else {
                this.notificationSvc.error('Unable to get a loan ID from your application.')
            }

        },
            (err) => {
                this.notificationSvc.error('There was an error starting your loan application.  Please contact your agent directly.')
                console.log('Error starting Loan Application');
                console.log(err);
                this.isLoading$.next(false);
            }
        );
    }


    buildApplicationTasks(): LgvLoanApp.Task[] {
        let rtnTasks = [];
        rtnTasks = rtnTasks.concat(this.taskList$.value);
       
        if(this.lgvUserSvc.isPreLicensedAgent) {
            // use Generic/Other Task set as default for prelicensed agents
            this.taskSetField.setValue('efce3896-71d3-ee11-85f7-6045bd020c58');
        }

        let taskItemsFromSet = this.lgvLoanAppSvc.taskItemsForSet(this.taskSetField.value);

        if(this.lgvUserSvc.isPreLicensedAgent && this._form.value?.loanPurpose === 'Refinance') {
            // add required task items for prelicensed refinance loan
            const addtlTasks = this.lgvLoanAppSvc.taskItems$?.value.filter(
                taskItem => taskItem?.id == '1d485c94-33db-ee11-85f7-00224802beb3' || 
                    taskItem?.id == '1e485c94-33db-ee11-85f7-00224802beb3' || 
                    taskItem?.id == '22485c94-33db-ee11-85f7-00224802beb3'
            );
            taskItemsFromSet = taskItemsFromSet.concat(addtlTasks);
        }

        // filter out duplicate tasks from the two lists.
        for (let ti of taskItemsFromSet) {
            if (!rtnTasks.find(task => task.item.id === ti.id) || !this.lgvLoanAppSvc.isForbiddenDuplicateTaskItems(ti.id)) {

                // use a clone so we don't alter the original when removing the type
                let taskItemClone = structuredClone(ti);

                // convert TaskItems to Tasks

                let appTask: LgvLoanApp.Task = {
                    id: null,
                    name: taskItemClone.name,
                    description: taskItemClone.description,
                    item: null,
                    displayOrder: null,
                    type: taskItemClone.type
                }

                // appTask.item can not have a type or the api will 500.  Use it above for the task's type, then remove it from the item before adding
                if (taskItemClone.type) {
                    delete taskItemClone.type;
                }
                appTask.item = taskItemClone;

                rtnTasks.push(appTask);
            }
            else {
                console.log('Filtering out duplicate task:');
                console.log(ti);
            }
        }
        // console.log('finalTasks:');
        // console.log(rtnTasks);

        const loanAppTaskExists = rtnTasks.some(task => task.name === "Loan Application (1003)");

        if (!loanAppTaskExists) {
            const loanAppTask: LgvLoanApp.Task = {
                id: null,
                name: "Loan Application (1003)",
                description: "Loan Application (1003)",
                item: {
                    id: "96930fd5-66db-ee11-85f7-00224802beb3",
                    name: "Loan Application (1003)"
                },
                displayOrder: null,
                type: {
                    id: "e599535a-03d2-ee11-85f7-6045bd020c58",
                    name: "Form"
                }
            };
            rtnTasks.push(loanAppTask);
            console.log('Added missing task: Loan Application (1003)');
        }
        console.log('here tasks',rtnTasks)
        return rtnTasks;
    }

    onAssistantSelectionMade(user: LgvModel.UserDTO) {
        this.selectedAssistant$.next(user);
        this.assistantField.setValue(user ? user.id : null);
        this.assistantField.updateValueAndValidity();
    }

    onProcessorSelectionMade(user: LgvModel.UserDTO) {
        this.selectedProcessor$.next(user);
        this.processorField.setValue(user ? user.id : null);
        this.processorField.updateValueAndValidity();
    }

    onPropertySelected(propId: string) {
        this.selectedLoan$.next(null);
        console.log('Selected Property:');
        console.log(propId);

        if (!propId || propId === "TBD") {
            this.selectedProperty$.next(null);
            this.loans$.next([]);
            this.loanPurposeField.setValue('');
            return;
        }

        let p = this.properties$.value.find(f => f.propertyInfo.id === propId);
        if (p) {
            this.selectedProperty$.next(p);
            if ( p.associationType === "Owned") {
                this.loanPurposeField.setValue('Refinance');
                this.setTaskSet('Refinance');
            }
            else if (p.associationType === "Prospect"){
                this.loanPurposeField.setValue('Purchase');
            }
            else{
                this.loanPurposeField.setValue('');
            }
            this.stateField.setValue(p?.propertyInfo?.state);

            this.loans$.next(p.loans.filter(l => l.lienType?.name.startsWith("First")));
            console.log(p);
        }
    }

    onLoanSelected(loanId: string) {
        console.log('Selected Loan:');
        console.log(loanId);

        if (!loanId || loanId === "TBD") {
            this.selectedLoan$.next(null);
            return;
        }

        let l = this.loans$.value.find(f => f.loanInfo.id === loanId);
        if (l) {
            this.selectedLoan$.next(l);
            console.log(l);
        }

    }

    openInfoPopup(key: string): void {
        const dialogRef = this.matDialog.open(InfoPopup, {
            width: '500px',
            data: { mode: key }
        });
    }

    openTaskModal() {
        const dialogRef = this.matDialog.open(LoanAppTaskModalComponent, {
            width: '800px',
            panelClass: 'dialog-container',
            //disableClose: true,
            data: {
                clientId: this.clientInfo$.value?.id,
                agentDTO: this.agentDTO,
                mode: 'addTask',
                isReturnOnly: true
            }
        });

        dialogRef.afterClosed().pipe(first()).subscribe(x => {
            if (x?.action === true) {
                if (x?.task) {
                    let tempTasks = this.taskList$.value;

                    tempTasks.push(x.task);
                    this.taskList$.next(tempTasks);

                    console.log('new tasks:');
                    console.log(tempTasks);
                }
            }
        });

    }


    //onAddTaskToQueue() {
    //    let model = this._taskForm.getRawValue();
    //    console.log(model);
    //    this.taskList.push(model);
    //    this.pageMode$.next('loanApp');
    //}

    onRemoveTask(t: LgvLoanApp.Task) {

        const tmpArray = this.taskList$.value;

        /* note: We don't have a true Task.id yet, so use item ID */
        const remIndex = tmpArray.findIndex(x => x.item.id == t.item.id);

        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1)
            this.taskList$.next(tmpArray);
        }
    }

    onOpenTaskSets() {
        this.pageMode$.next('taskSets');
    }

    onReturnToLoanApp() {
        this.pageMode$.next('loanApp');
    }

    setTaskSet(taskSetName: string) {
        const taskSet = this.taskSets.find(ts => ts.name === taskSetName);
        if (taskSet) {
            this.taskSetField.setValue(taskSet.id);
        } else {
            console.warn(`Task set with name ${taskSetName} not found`);
        }
    }


    onNoLicensedStates(noLicensedStates: boolean) {
        console.log('StartLoanModal: No licensed states', noLicensedStates);
        
        if (!this._selectedAgentId.value && !noLicensedStates) {
            console.log('StartLoanModal: Agent cleared, resetting state');
            this._form.get('state').setValue(null);
            this._hasShownNoLicensesWarning = false;
            
            const errors = this._form.get('state').errors;
            if (errors && errors['noLicensedStates']) {
                delete errors['noLicensedStates'];
                this._form.get('state').setErrors(Object.keys(errors).length === 0 ? null : errors);
            }
            return;
        }
        
        if (noLicensedStates) {
            this._form.get('state').setErrors({ 'noLicensedStates': true });
            
            if (this._selectedAgentId.value && !this._hasShownNoLicensesWarning) {
                const agentName = this.agentDTO && this.agentDTO.agentInfo ? 
                    `${this.agentDTO.agentInfo.firstName} ${this.agentDTO.agentInfo.lastName}` : 'Selected user';
                this.notificationSvc.warn(`${agentName} has no active licenses. Please select a different user or contact support.`);
                this._hasShownNoLicensesWarning = true;
            }
        } else {
            const errors = this._form.get('state').errors;
            if (errors && errors['noLicensedStates']) {
                delete errors['noLicensedStates'];
                this._form.get('state').setErrors(Object.keys(errors).length === 0 ? null : errors);
            }
            this._hasShownNoLicensesWarning = false;
        }
    }
    
    private _hasShownNoLicensesWarning = false;
}

function ValidStateCode(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) return null;
        
        // Use getStateName to check if the state exists - if it returns empty string, state is invalid
        const stateName = LgvStatesHelper.getStateName(value.toUpperCase());
        return !stateName ? { 'invalidStateCode': true } : null;
    }
}
